import { useState, useCallback, useEffect } from 'react';
import './index.css';
import { ToastContext } from './context/ToastContext';

import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from './firebase';
import { useFirestoreData } from './hooks/useFirestoreData';
import Login from './components/Login';
import NicknameSetup from './components/NicknameSetup';
import Home from './components/Home';
import AddRecord from './components/AddRecord';
import Calendar from './components/Calendar';
import More from './components/More';
import Analysis from './components/analysis/Analysis';
import Social from './components/Social';
import Notifications from './components/Notifications';
import { calcStreak } from './utils/streak';

export default function App() {
    const initialTab = (() => {
        const value = new URLSearchParams(window.location.search).get('tab');
        return ['goals', 'cal', 'home', 'social', 'more', 'add'].includes(value) ? value : 'home';
    })();
    const [tab, setTabState] = useState(initialTab);
    const [toast, setToast] = useState({ msg: '', show: false });
    const [user, setUser] = useState(undefined); // undefined=로딩중, null=비로그인

    useEffect(() => {
        return onAuthStateChanged(auth, u => setUser(u ?? null));
    }, []);

    useEffect(() => {
        const onPop = () => {
            const value = new URLSearchParams(window.location.search).get('tab');
            setTabState(['goals', 'cal', 'home', 'social', 'more', 'add'].includes(value) ? value : 'home');
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, []);

    const { records, setRecords, weights, setWeights, templates, setTemplates, goals, setGoals, promotions, setPromotions, nickname, setNickname, profile, isProfileComplete, saveProfileSetup, userId, checkUserIdAvailable, updateUserId, friends, friendRequests, sentFriendRequests, notifications, addFriend, removeFriend, getFriendData, acceptFriendRequest, declineFriendRequest, cancelFriendRequest, markNotificationRead, markAllNotificationsRead, createPost, createPromotionPost, updatePromotionPost, deletePromotionPostForPromotion, updatePost, deletePost, deletePostForRecord, toggleLike, loading } =
        useFirestoreData(user?.uid);

    // URL ?add=userId 파라미터로 친구 추가 처리
    // userId가 Firestore에서 로드된 시점(= 로그인 + 데이터 준비 완료)에 1회 실행
    const [pendingAddId] = useState(() => new URLSearchParams(window.location.search).get('add'));
    useEffect(() => {
        if (!pendingAddId || !userId) return;
        window.history.replaceState({}, '', window.location.pathname);
        addFriend(pendingAddId, friends)
            .then(f => {
                showToast(`${f.nickname}님에게 친구 요청을 보냈어요!`);
                setTab('social');
            })
            .catch(e => {
                const msgs = { not_found: '존재하지 않는 ID예요', self: '본인은 추가할 수 없어요', already: '이미 친구예요', pending: '이미 친구 요청을 보냈어요' };
                showToast(msgs[e.message] || '친구 추가에 실패했어요');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId]);

    const showToast = useCallback((msg) => {
        setToast({ msg, show: true });
        setTimeout(() => setToast(t => ({ ...t, show: false })), 2200);
    }, []);

    const setTab = useCallback((nextTab) => {
        setTabState(nextTab);
        const url = new URL(window.location.href);
        url.searchParams.set('tab', nextTab);
        ['date', 'record', 'promotion', 'month'].forEach(key => url.searchParams.delete(key));
        window.history.pushState({}, '', `${url.pathname}${url.search}`);
    }, []);

    const now = new Date();
    const dateStr = now.toLocaleDateString('ko-KR', {
        year: 'numeric', month: 'long', day: 'numeric', weekday: 'short',
    });

    const streak = calcStreak(records);

    const tabs = [
        {
            id: 'goals', label: '분석',
            icon: <><polyline points="4 17 8 13 12 15 17 9 20 12" /><line x1="4" y1="20" x2="20" y2="20" /></>
        },
        {
            id: 'cal', label: '기록',
            icon: <><rect x="3" y="4" width="16" height="16" rx="2.5" /><line x1="7" y1="2" x2="7" y2="6" /><line x1="15" y1="2" x2="15" y2="6" /><line x1="3" y1="9" x2="19" y2="9" /></>
        },
        {
            id: 'home', label: '홈',
            icon: <path d="M3 9.5L11 3l8 6.5V19a1 1 0 01-1 1H4a1 1 0 01-1-1z" />,
            extra: <path d="M8 20v-8h6v8" />
        },
        {
            id: 'social', label: '소셜',
            icon: <><circle cx="8" cy="8" r="3" /><circle cx="15" cy="8" r="3" /><path d="M2 20c0-3.3 2.7-6 6-6h1" /><path d="M13 14h1c3.3 0 6 2.7 6 6" /></>
        },
        {
            id: 'more', label: '더보기',
            icon: <><circle cx="11" cy="6" r="1.2" fill="currentColor" /><circle cx="11" cy="11" r="1.2" fill="currentColor" /><circle cx="11" cy="16" r="1.2" fill="currentColor" /></>
        },
    ];

    const pageHeaders = {
        add: { title: '기록 추가', sub: '운동과 체중을 빠르게 기록해요' },
        goals: { title: '분석', sub: '운동 데이터를 종목별로 분석해요' },
        cal: { title: '기록', sub: '달력으로 지난 훈련을 돌아봐요' },
        social: { title: '소셜', sub: '친구들의 운동 피드를 확인해요' },
        more: { title: '더보기', sub: '계정과 데이터 관리' },
    };

    const [pendingEdit, setPendingEdit] = useState(null);
    const [pendingDate, setPendingDate] = useState(null);

    const onLogout = () => signOut(auth);
    const sharedProps = { records, setRecords, weights, setWeights, templates, setTemplates, goals, setGoals, promotions, setPromotions, nickname, setNickname, profile, userId, checkUserIdAvailable, updateUserId, friends, friendRequests, sentFriendRequests, notifications, addFriend, removeFriend, getFriendData, acceptFriendRequest, declineFriendRequest, cancelFriendRequest, markNotificationRead, markAllNotificationsRead, createPost, createPromotionPost, updatePromotionPost, deletePromotionPostForPromotion, updatePost, deletePost, deletePostForRecord, toggleLike, showToast, setTab, pendingEdit, setPendingEdit, pendingDate, setPendingDate, user, onLogout };

    if (user === undefined || loading) {
        return <div className="app"><div className="loading">···</div></div>;
    }
    if (!user) return <Login />;
    if (!isProfileComplete) {
        return (
            <NicknameSetup
                defaultName={nickname || user.displayName}
                defaultEmail={profile?.email || user.email}
                profile={profile}
                onSave={saveProfileSetup}
            />
        );
    }

    return (
        <ToastContext.Provider value={showToast}>
        <div className="app">
            {/* Toast */}
            <div className={`toast ${toast.show ? 'show' : ''}`}>{toast.msg}</div>

            {/* Header */}
            {tab === 'home' ? (
                <div className="header home-header">
                    <div className="home-header-main">
                        <div>
                            <div className="header-date">{dateStr}</div>
                            <div className="header-title">{nickname}님의 운동 일지</div>
                            {streak > 0 && (
                                <div className="header-streak">
                                    <div className="streak-dot" />
                                    <span>{streak}일 연속 운동중!</span>
                                </div>
                            )}
                        </div>
                        <Notifications
                            notifications={notifications}
                            friendRequests={friendRequests}
                            friends={friends}
                            getFriendData={getFriendData}
                            acceptFriendRequest={acceptFriendRequest}
                            declineFriendRequest={declineFriendRequest}
                            markNotificationRead={markNotificationRead}
                            markAllNotificationsRead={markAllNotificationsRead}
                            showToast={showToast}
                        />
                    </div>
                </div>
            ) : (
                <div className="page-header">
                    <div>
                        <div className="page-title">{pageHeaders[tab]?.title}</div>
                        <div className="page-subtitle">{pageHeaders[tab]?.sub}</div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="content">
                {tab === 'home' && <Home  {...sharedProps} />}
                {tab === 'add' && <AddRecord {...sharedProps} />}
                {tab === 'goals' && <Analysis records={records} weights={weights} promotions={promotions} />}
                {tab === 'cal' && <Calendar  {...sharedProps} />}
                {tab === 'social' && <Social {...sharedProps} />}
                {tab === 'more' && <More      {...sharedProps} />}
            </div>

            {(tab === 'home' || tab === 'cal') && (
                <button className="fab-add" onClick={() => setTab('add')} aria-label="운동 추가">
                    <span>+</span>
                </button>
            )}

            {/* Bottom Nav */}
            <nav className="nav">
                {tabs.map(t => (
                    <button
                        key={t.id}
                        className={`nav-item ${tab === t.id ? 'active' : ''} ${t.id === 'home' ? 'home-nav-item' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        <svg className="nav-icon" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.3">
                            {t.icon}
                            {t.extra}
                        </svg>
                        {t.id !== 'home' && <span>{t.label}</span>}
                    </button>
                ))}
            </nav>
        </div>
        </ToastContext.Provider>
    );
}
